import type { Dispatch, SetStateAction } from 'react';

import { useQuery } from 'react-query';

import { createLocale } from 'locales';

import type { AvailableSingleCallJob } from 'state/remote/types';

const localize = createLocale('freelancerAvailableJobsLocales');

type FreelancerAvailaleJobs = {
  data: [string, AvailableSingleCallJob[]][];
  isLoading?: boolean;
  error: Error | null;
  personalInvitesNumber: number;
  refetch: (options: {
    throwOnError?: boolean;
    cancelRefetch?: boolean;
  }) => unknown;
};

const groupJobsPerDay = (
  jobsArray: Array<AvailableSingleCallJob>,
): [string, AvailableSingleCallJob[]][] => {
  const jobsPerDay: { [key: string]: AvailableSingleCallJob[] } = {};

  jobsArray.forEach((job) => {
    if (jobsPerDay[job.date]) {
      jobsPerDay[job.date].push(job);
    } else {
      jobsPerDay[job.date] = [job];
    }
  });

  // after grouping the jobs by date, sort them. first based on start_time, then based on travel distance
  return Object.entries(jobsPerDay).map(([date, jobs]) => {
    return [
      date,
      jobs.sort((a, b) => {
        if (a.start === b.start) {
          return a.travel_distance - b.travel_distance;
        }
        return a.start < b.start ? -1 : 1;
      }),
    ];
  });
};

const useGetAvailableSingleCallJobs = (
  isFilterEnabled: boolean,
  minRate: string,
  wholeDays: boolean,
  travelDistance: string,
  newjob: boolean,
  ifAvailable: boolean,
  type: string,
  organisationId: number | undefined,
  competences: string[],
  filterInitiated: boolean,
  setWarningMessage: Dispatch<SetStateAction<string>>,
  postCode?: string,
): FreelancerAvailaleJobs => {
  let personalInvitesNumber = 0;
  const getAvailableJobs = async () => {
    let response: Response;
    if (!isFilterEnabled) {
      response = await fetch('/api/jobs/available/');
    } else {
      const parameters = {
        competences: competences.join(','),
        if_available: ifAvailable ? 'true' : 'false',
        min_rate: minRate,
        new: newjob ? 'true' : 'false',
        organisation: organisationId?.toString() || '',
        post_code: postCode || '',
        travel_distance: travelDistance,
        type,
        whole_days: wholeDays ? 'true' : 'false',
      };
      response = await fetch(
        `/api/jobs/available/?${new URLSearchParams(parameters).toString()}`,
      );
    }
    if (response.ok) {
      const dataR = response.json();
      const res = await dataR.then((result) => {
        if (result?.warning) return result.warning;
        if (result?.length === 0) return localize('noAvailableJobs');
        return 'ok';
      });
      if (res === 'ok') {
        return dataR;
      }
      setWarningMessage(res);
      return dataR;
    }
    throw new Error('Something went wrong');
  };

  const { isLoading, error, data, refetch } = useQuery<
    Array<AvailableSingleCallJob>,
    Error
  >({
    queryFn: getAvailableJobs,
    queryKey: ['get-available-single-call-jobs', filterInitiated],
  });
  if (error) {
    return {
      data: [] as [string, AvailableSingleCallJob[]][],
      error,
      isLoading: false,
      personalInvitesNumber,
      refetch,
    };
  }
  if (data && data.length)
    personalInvitesNumber = data.filter((job) => !!job.is_personal).length;

  let newdata = [] as [string, AvailableSingleCallJob[]][];
  if (data && data?.length > 0) {
    newdata = groupJobsPerDay(data);
  }

  return {
    data: newdata ?? [],
    error,
    isLoading,
    personalInvitesNumber,
    refetch,
  };
};

export default useGetAvailableSingleCallJobs;
